import type { AiApplicationDto } from '@terra/shared-contracts-ai-applications';
import type { GetAllApplicationsResponseDto } from '@terra/shared-contracts-applications';
import type { ExternalNetworkDto } from '@terra/shared-contracts-external-network';
import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import type { Application } from '../store/projectStore';
import { TARGET_TYPE } from './target-scope.utils';

type WebApplicationListItem = GetAllApplicationsResponseDto[number];
type ExternalNetworkListItem = ExternalNetworkDto;
type InternalNetworkListItem = InternalNetworkDto;

export function mapWebApplicationToPortalTarget(
  application: WebApplicationListItem,
): Application {
  return {
    ...application,
    targetType: TARGET_TYPE.WEB_APPLICATION,
  };
}

export function mapAiApplicationToPortalTarget(
  application: AiApplicationDto,
): Application {
  return {
    id: application.id,
    targetType: TARGET_TYPE.AI_APPLICATION,
    tenantId: application.tenantId,
    name: application.name,
    url: application.url,
    documentationUrls: application.documentationUrls,
    environment: application.environment,
    owner: application.owner,
    isInternalApp: null,
    isBackendOnly: false,
    smokeTestEnabled: application.smokeTestEnabled,
    aiKind: application.kind,
    domains: [],
  };
}

export function mapExternalNetworkToPortalTarget(
  surface: ExternalNetworkListItem,
): Application {
  return {
    id: surface.id,
    targetType: TARGET_TYPE.EXTERNAL_NETWORK,
    tenantId: surface.tenantId,
    name: surface.name,
    url: '',
    environment: '',
    owner: null,
    isInternalApp: null,
    isBackendOnly: false,
    smokeTestEnabled: surface.smokeTestEnabled,
    organizationName: surface.organizationName ?? null,
    domains: [],
  };
}

export function mapInternalNetworkToPortalTarget(
  surface: InternalNetworkListItem,
): Application {
  return {
    id: surface.id,
    targetType: TARGET_TYPE.INTERNAL_NETWORK,
    tenantId: null,
    name: surface.name,
    url: surface.domainNames[0] ?? '',
    environment: '',
    owner: null,
    isInternalApp: true,
    isBackendOnly: false,
    smokeTestEnabled: surface.smokeTestEnabled,
    domainNames: surface.domainNames,
    domains: [],
  };
}

export function mergePortalApplications({
  webApplications,
  aiApplications,
}: {
  webApplications: WebApplicationListItem[];
  aiApplications: AiApplicationDto[];
}): Application[] {
  return mergePortalTargets({
    webApplications,
    aiApplications,
    externalSurfaces: [],
    internalNetworks: [],
  });
}

export function mergePortalTargets({
  webApplications,
  aiApplications,
  externalSurfaces,
  internalNetworks,
}: {
  webApplications: WebApplicationListItem[];
  aiApplications: AiApplicationDto[];
  externalSurfaces: ExternalNetworkListItem[];
  internalNetworks: InternalNetworkListItem[];
}): Application[] {
  const webById = new Map(
    webApplications.map((application) => [application.id, application]),
  );

  const merged: Application[] = webApplications.map(
    mapWebApplicationToPortalTarget,
  );

  for (const aiApplication of aiApplications) {
    if (webById.has(aiApplication.id)) {
      continue;
    }

    merged.push(mapAiApplicationToPortalTarget(aiApplication));
  }

  for (const externalSurface of externalSurfaces) {
    merged.push(mapExternalNetworkToPortalTarget(externalSurface));
  }

  for (const internalNetwork of internalNetworks) {
    merged.push(mapInternalNetworkToPortalTarget(internalNetwork));
  }

  return merged.sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
  );
}
